import {
  CACHE_SIGNALS_LIST,
  FETCH_SIGNALS_LIST_FAILURE,
  FETCH_SIGNALS_LIST_REQUEST,
  FETCH_SIGNALS_LIST_SUCCESS,
  SIGNALS_LIST_CHANGE_COMMON,
  SIGNALS_LIST_CHANGE_DELETE_INIT,
  SIGNALS_LIST_CHANGE_EXPIRY_INIT,
  SIGNALS_LIST_CHANGE_MODAL_ERROR,
  SIGNALS_LIST_CHANGE_RENAME_INIT,
  SIGNALS_LIST_TABLE_MULTI_SELECT,
  SIGNALS_LIST_TABLE_SETTINGS_SAVE_COLUMN_TOGGLE,
  SIGNALS_LIST_TABLE_SHIFT_PRESSED,
  SIGNALS_LIST_TABLE_SORTING,
} from '../../../constants/ActionTypes';
import { setAuthorizationHeader, signals } from '../../../api';
import storage from '../../../storage';
import { timeoutPromise } from '../../../helpers/promises';
import {
  getIsSignalsListFetching,
  getSignalsList,
  getSignalsListTableColumnToggleData,
  getSignalsListTableSorting,
} from './getters';
import { SIGNALS_LIST_TABLE_SORT } from '../../../constants/cookies';

/**
 * Fetches a list of signal groups from the API and dispatches Redux actions based on the result.
 *
 * @param {'tv' | 'custom'} [type='tv'] - The type of signals to fetch. Can be 'tv' or 'custom'.
 * @param {number} [page=1] - The page number to fetch.
 * @returns {Function} A Redux thunk that fetches signal data and dispatches appropriate actions.
 */
export const fetchSignalsList =
  (type = 'tv', page = 1) =>
  async (dispatch, getState) => {
    try {
      if (getIsSignalsListFetching(getState())) {
        return Promise.resolve();
      }

      setAuthorizationHeader(storage.getItem('token'));

      dispatch({
        type: FETCH_SIGNALS_LIST_REQUEST,
      });

      let data = await signals.signalGroups(type, page);

      if (!data?.data?.data?.length && page !== 1) {
        data = await signals.signalGroups(type, 1);
      }

      dispatch({
        type: FETCH_SIGNALS_LIST_SUCCESS,
        data,
      });

      return Promise.resolve();
    } catch (error) {
      dispatch({
        type: FETCH_SIGNALS_LIST_FAILURE,
        data: {
          error,
        },
      });

      return Promise.reject(error);
    }
  };

export const cacheSignalsList = () => async (dispatch, getState) => {
  await timeoutPromise(50);
  const cachedData = getSignalsList(getState());

  dispatch({
    type: CACHE_SIGNALS_LIST,
    data: cachedData,
  });

  return Promise.resolve();
};

export const signalsListTableclearSort = () => (dispatch) => {
  storage.deleteItem(SIGNALS_LIST_TABLE_SORT);

  dispatch({
    type: SIGNALS_LIST_TABLE_SORTING,
    data: {},
  });

  return Promise.resolve();
};

export const saveSignalsListTableSetttingsColumnToggle =
  (field, value) => (dispatch, getState) => {
    const tableSortField = getSignalsListTableSorting(getState())?.field;
    const originalData = getSignalsListTableColumnToggleData(getState()) || [];
    const newData = [...originalData];

    const targetIndex = newData.findIndex((item) => item.name === field);

    if (targetIndex < 0) {
      return Promise.resolve();
    }

    newData[targetIndex].showColumn = value;

    const isSortFieldInHiddenColumns = newData.some(
      (item) => item.name === tableSortField
    );

    if (tableSortField && isSortFieldInHiddenColumns) {
      dispatch(signalsListTableclearSort());
    }

    dispatch({
      type: SIGNALS_LIST_TABLE_SETTINGS_SAVE_COLUMN_TOGGLE,
      data: newData,
    });

    return Promise.resolve();
  };

export const signalsListMultiSelect = (id) => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_TABLE_MULTI_SELECT,
    data: {
      id,
    },
  });

  return Promise.resolve();
};

export const signalsListMultiSelectClear = () => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_TABLE_MULTI_SELECT,
    data: {
      id: '',
    },
  });

  return Promise.resolve();
};

export const signalsCheckboxShiftPress =
  (data = false) =>
  (dispatch) => {
    dispatch({
      type: SIGNALS_LIST_TABLE_SHIFT_PRESSED,
      data,
    });

    return Promise.resolve();
  };

export const signalsTablechangeSort = (field) => (dispatch) => {
  let newSort = {};
  const cacheSort = storage.getItem(SIGNALS_LIST_TABLE_SORT);

  if (!cacheSort.field || (cacheSort.field && cacheSort.field !== field)) {
    newSort = {
      field,
      order: 'ASC',
    };
  } else if (cacheSort.field === field && cacheSort.order === 'ASC') {
    newSort = {
      field,
      order: 'DSC',
    };
  }

  if (newSort.field && newSort.order) {
    storage.setItems({
      [SIGNALS_LIST_TABLE_SORT]: newSort,
    });
  } else {
    storage.deleteItem(SIGNALS_LIST_TABLE_SORT);
  }

  dispatch({
    type: SIGNALS_LIST_TABLE_SORTING,
    data: newSort,
  });

  return Promise.resolve();
};

export const clearSignalListModalErrors = () => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_MODAL_ERROR,
    data: {
      showError: false,
      error: {},
    },
  });

  return Promise.resolve();
};

export const toggleSignal =
  (id, toggleCondition = false) =>
  async (dispatch) => {
    try {
      dispatch({
        type: SIGNALS_LIST_CHANGE_COMMON,
        updatedData: [{ _id: id, enabledLoading: true }],
      });

      await timeoutPromise(200);

      const toggle = toggleCondition ? 'enable' : 'disable';
      setAuthorizationHeader(storage.getItem('token'));
      const res = await signals.toggleSignal(id, toggle);

      const isSuccess =
        res && res.data && res.data.status && res.data.status === 'success'
          ? true
          : false;

      if (isSuccess) {
        dispatch({
          type: SIGNALS_LIST_CHANGE_COMMON,
          updatedData: [{ _id: id, enabled: toggleCondition }],
        });
      }

      await timeoutPromise(500);

      return Promise.resolve({
        isSuccess,
        enabled: toggleCondition,
      });
    } catch (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message &&
        error.response.data.message.trim() !== ''
      ) {
        const err =
          error && error.response && error.response.data
            ? error.response.data
            : {};

        dispatch({
          type: SIGNALS_LIST_CHANGE_MODAL_ERROR,
          data: {
            showError: true,
            error: err,
          },
        });

        return Promise.reject(null);
      }

      return Promise.reject(error);
    } finally {
      dispatch({
        type: SIGNALS_LIST_CHANGE_COMMON,
        updatedData: [{ _id: id, enabledLoading: false }],
      });
    }
  };

export const renameSignalStart = (id, name) => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_RENAME_INIT,
    data: {
      start: true,
      id,
      name,
    },
  });

  return Promise.resolve();
};

export const renameSignalClear = () => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_RENAME_INIT,
    data: {
      start: false,
      id: '',
      name: '',
    },
  });

  return Promise.resolve();
};

export const renameSignal = (id, name) => async (dispatch) => {
  try {
    setAuthorizationHeader(storage.getItem('token'));

    const res = await signals.renameSignal(id, name);
    const data = res && res.data && res.data.data ? res.data.data : null;

    if (data._id) {
      dispatch({
        type: SIGNALS_LIST_CHANGE_COMMON,
        updatedData: [{ _id: data._id, name }],
        isUpdateDate: false,
      });
    }

    return Promise.resolve();
  } catch (error) {
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.message &&
      error.response.data.message.trim() !== ''
    ) {
      const err =
        error && error.response && error.response.data
          ? error.response.data
          : {};

      dispatch({
        type: SIGNALS_LIST_CHANGE_MODAL_ERROR,
        data: {
          showError: true,
          error: err,
        },
      });

      return Promise.reject(null);
    }

    return Promise.reject(error);
  }
};

export const deleteSignalStart = (id) => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_DELETE_INIT,
    data: {
      start: true,
      id,
    },
  });

  return Promise.resolve();
};

export const deleteSignalClear = () => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_DELETE_INIT,
    data: {
      start: false,
      id: '',
    },
  });

  return Promise.resolve();
};

export const deleteSignal = (id) => async (dispatch) => {
  try {
    setAuthorizationHeader(storage.getItem('token'));

    await signals.deleteSignal(id);

    return Promise.resolve();
  } catch (error) {
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.message &&
      error.response.data.message.trim() !== ''
    ) {
      const err =
        error && error.response && error.response.data
          ? error.response.data
          : {};

      dispatch({
        type: SIGNALS_LIST_CHANGE_MODAL_ERROR,
        data: {
          showError: true,
          error: err,
        },
      });

      return Promise.reject(null);
    }

    return Promise.reject(error);
  }
};

export const updateExpirySignalStart = (id, expiry) => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_EXPIRY_INIT,
    data: {
      start: true,
      id,
      expiry,
    },
  });

  return Promise.resolve();
};

export const updateExpirySignalClear = () => (dispatch) => {
  dispatch({
    type: SIGNALS_LIST_CHANGE_EXPIRY_INIT,
    data: {
      start: false,
      id: '',
      expiry: undefined,
    },
  });

  return Promise.resolve();
};

export const updateExpirySignal = (id, expiry) => async (dispatch) => {
  try {
    setAuthorizationHeader(storage.getItem('token'));

    const res = await signals.updateExpiry(id, expiry);
    const data = res && res.data && res.data.data ? res.data.data : null;

    if (data._id) {
      dispatch({
        type: SIGNALS_LIST_CHANGE_COMMON,
        updatedData: [{ _id: data._id, expiry }],
        isUpdateDate: false,
      });
    }

    return Promise.resolve();
  } catch (error) {
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.message &&
      error.response.data.message.trim() !== ''
    ) {
      const err =
        error && error.response && error.response.data
          ? error.response.data
          : {};

      dispatch({
        type: SIGNALS_LIST_CHANGE_MODAL_ERROR,
        data: {
          showError: true,
          error: err,
        },
      });

      return Promise.reject(null);
    }

    return Promise.reject(error);
  }
};
